<script>
import signalConfig, { PREFERRED_SIGNAL_ORDER } from '@/constants/signalConfig';
import * as signalNames from '@/constants/signalNames';
import {
  getInstanceId,
  getSignalLink,
  getSignalName,
  getSignalSubtitle,
} from '@/utils/signalNames';
import SignalButton from '@/V2/documents/document-view/SignalButton';
import { mapGetters, mapMutations } from 'vuex';
import { downloadDocument } from '@/utils/download';

import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
  iBadge,
  iBadgeColours,
  iIcon,
  iIconButton,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iSwitch,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iAccordion,
    iAccordionItem,
    iBadge,
    iIcon,
    iIconButton,
    iMenu,
    iMenuItem,
    iSwitch,
    iTooltip,
    SignalButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iAccordionItemSizes,
      iBadgeColours,
      iMenuTriggerAlignOptions,
      iTooltipAlignmentMode,
      palette,
      signalNames,
      learnMoreBaseLink: 'https://help.inscribe.ai/en/articles/',
      learnMoreLink:
        'https://help.inscribe.ai/en/articles/7852994-understanding-inscribe-fraud-signals',
    };
  },
  computed: {
    /**
     * this method creates a grouping of signals accoring to the signalName property
     * Each group can contains an Array of flatSignals
     */
    signalsGroups() {
      const groups = [];
      Object.keys(this.document.fraud_results || {}).forEach((signalKey) => {
        const fraud_result = this.document.fraud_results[signalKey];
        if (fraud_result.failed) {
          const baseRoute = {
            customer_id:
              this.$route.params.customer_id || this.document?.customer_id,
            document_id: this.$route.params.document_id || this.document.id,
            query: this.$route.query,
          };

          // add instanceId
          fraud_result.details?.pages?.forEach((page) => {
            page.instances?.forEach((instance) => {
              instance.id = getInstanceId(
                instance,
                page.page_number,
                signalKey,
              );
              instance.enabled =
                !this.getDisabledInstances().includes(instance.id) &&
                !this.getDisabledGroups().includes(signalKey);
              instance.link = {
                ...baseRoute,
                query: {
                  ...this.$route.query,
                  documentPage: page.page_number,
                  instance: instance.id,
                },
                name: 'document-signals',
              };
            });
          });

          const currentSignalConfig = signalConfig[signalKey];
          const flattenSignal = {
            ...currentSignalConfig,
            signalKey,
            ...fraud_result,
            ...fraud_result.details,
            pages: fraud_result.details.pages,
          };
          flattenSignal.pages?.sort((p1, p2) =>
            p1.page_number < p2.page_number ? -1 : 1,
          );

          // 1 for each type, understand which is the grouping property, group by signalName
          let currentGroup = !!groups.find((g) => g.id === signalKey);
          if (!currentGroup) {
            // create group with correct data
            currentGroup = {
              documentId: this.document.id,
              customerId: this.document.customer_id,
              signalKey,
              helpArticle: currentSignalConfig.helpArticle,
              title: getSignalName(signalKey),
              subTitle: getSignalSubtitle(flattenSignal),
              signals: [],
              hasRegions:
                fraud_result.details.pages?.length > 0 &&
                ![signalNames.TEMPLATES, signalNames.BLOCKLISTS].includes(
                  signalKey,
                ),
              link: getSignalLink(flattenSignal, baseRoute),
              enabled: !this.getDisabledGroups().includes(signalKey),
            };
            groups.push(currentGroup);
          }

          // 2 for each fraud_result, flatten it to a signal element
          currentGroup.signals.push(flattenSignal);
        }
      });

      // sort by preferred order
      groups.sort((g1, g2) => {
        return PREFERRED_SIGNAL_ORDER.indexOf(g1.signalKey) >
          PREFERRED_SIGNAL_ORDER.indexOf(g2.signalKey)
          ? 1
          : -1;
      });

      return groups;
    },
  },
  methods: {
    ...mapGetters({
      getDisabledInstances: 'getDisabledInstances',
      getDisabledGroups: 'getDisabledGroups',
    }),
    ...mapMutations(['setSelectedSignalsGroup', 'setDisabledGroup']),
    openDecisionEngine() {
      this.$router.push({ name: 'decision-engine-detectors' });
    },
    onButtonClicked(signalGroup) {
      if (signalGroup.hasRegions) {
        this.$emit('open-signal-details', signalGroup);
      } else {
        this.$router.push(signalGroup.link).catch(() => {});
      }
    },
    onMouseEvent(signalGroup, type) {
      this.setSelectedSignalsGroup(type === 'over' ? signalGroup : null);
    },
    onSignalGroupSwitch(value, signalGroup) {
      this.setDisabledGroup({ group: signalGroup, value });
    },
    stopPropagation(evt) {
      evt.stopPropagation();
    },
    downloadXray(signalGroup) {
      const recovered_version = signalGroup.signals[0].recovered_version;
      if (recovered_version) {
        downloadDocument(recovered_version.url, 'recovered-version.pdf');
      }
    },
  },
};
</script>

<template>
  <i-accordion data-testid="signals-list" class="signals-list">
    <i-accordion-item
      title="Fraud Signals"
      class="half-margin"
      :no-background="true"
      :mount-open="true"
      :size="iAccordionItemSizes.LARGE"
    >
      <!--title extra content-->
      <div
        slot="extra-title-content"
        class="extra-title-content d-flex flex-row align-items-center i-gap-4"
      >
        <!-- info icon -->
        <i-icon
          symbol="small-question-mark"
          :size="12"
          :colour="palette['light-neutral-80']"
          class="signals-list-help-icon"
        ></i-icon>
        <i-tooltip
          :target="`.signals-list-help-icon`"
          title="Fraud Signals"
          description="Document fraud signals are indicators of a potentially fraudulent document, such as modified information, abnormal formatting or font styles, and suspicious metadata."
          link-text="Learn more"
          :link-external-url="learnMoreLink"
          :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
        />

        <i-badge
          :label="signalsGroups.length + ' Detected'"
          :colour="iBadgeColours.NEUTRAL"
          :outline="true"
        ></i-badge>
      </div>

      <div class="document-signals-list d-flex flex-column accordion-content">
        <!-- empty state -->
        <document-right-panel-empty-state
          v-if="signalsGroups.length === 0"
          data-testid="signals-list-empty-state"
          symbol="medium-file-alert"
          title="No fraud signals detected"
          sub-title="Fraud signals display here when detected"
          button-text="Manage Detectors"
          class="i-mx-6"
          @on-button-click="openDecisionEngine"
        ></document-right-panel-empty-state>

        <!-- signals list -->
        <signal-button
          v-for="signalGroup in signalsGroups"
          :key="signalGroup.signalKey"
          :title="signalGroup.title"
          :subtitle="signalGroup.subTitle"
          :success="false"
          @button-click="onButtonClicked(signalGroup)"
          @mouse-over="onMouseEvent(signalGroup, 'over')"
          @mouse-leave="onMouseEvent(signalGroup, 'leave')"
        >
          <!-- signal tools-->
          <div
            slot="tools"
            class="signal-tools d-flex flex-row align-items-center i-pl-8 i-pr-6 i-py-2 i-gap-6"
            @click="stopPropagation"
          >
            <i-switch
              v-if="signalGroup.hasRegions"
              v-model="signalGroup.enabled"
              size="xs"
              @input="(e) => onSignalGroupSwitch(e, signalGroup)"
            />
            <i-menu
              :trigger-align="iMenuTriggerAlignOptions.RIGHT"
              :width="250"
            >
              <i-icon-button
                slot="trigger"
                class="trigger-button"
                data-testid="signal-actions-button"
                size="md"
                symbol="medium-more-menu-vertical"
              />

              <!-- Download  -->
              <i-menu-item
                v-if="signalGroup.signalKey === signalNames.XRAY"
                slot="items"
                label="Download"
                icon="small-download-button-2"
                @item-click="downloadXray(signalGroup)"
              ></i-menu-item>

              <!-- manage Signals -->
              <i-menu-item
                slot="items"
                label="Manage Detectors"
                icon="small-setting-cog"
                @item-click="openDecisionEngine()"
              ></i-menu-item>

              <!-- Learn More  -->
              <i-menu-item
                slot="items"
                label="Learn More"
                icon="small-info-circle"
                right-icon="small-arrows-expand"
                :right-icon-color="palette['light-neutral-70']"
                :link-external-url="
                  learnMoreBaseLink + `${signalGroup.helpArticle || ''}`
                "
              ></i-menu-item>
            </i-menu>
          </div>
        </signal-button>
      </div>
    </i-accordion-item>
  </i-accordion>
</template>
